import axios from 'axios';
import { getAuth } from "firebase/auth";
import { logger } from '../utils/logger';
import { baseAxiosConfig, applyVerticalOverrideInterceptor } from '../utils/apiConfig';

const localyticsAxios = applyVerticalOverrideInterceptor(axios.create(baseAxiosConfig));

/**
 * Localytics Score Service - Professional Business Analytics
 *
 * Clean mathematical methodology using actual business data:
 *
 * Core Metrics:
 * - Recent reviews (75% weight): Review growth momentum
 * - Sentiment (12% weight): Customer satisfaction
 * - Total reviews (8% weight): Market presence
 * - Website traffic (5% weight): Digital engagement
 *
 * Score Scale: 0-100 where 50 = median performance
 * - 80-100: Exceptional (top performers)
 * - 65-79: High performance
 * - 50-64: Above Average
 * - 35-49: Average
 * - 20-34: Below Average
 * - 0-19: Low performance
 */

/**
 * Get Localytics Scores for user's business portfolio
 * @param {Object} options - Scoring options
 * @param {string[]} options.businessNames - Specific business names to analyze (optional)
 * @returns {Promise<Object>} Localytics Score response with performance metrics
 */
export async function getLocalyticsScores(options = {}) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      logger.error('No authenticated user found');
      throw new Error('Authentication required');
    }

    const token = await user.getIdToken();

    const requestData = {
      user: user.email,
      business_names: options.businessNames || null,
      include_competitors: false,
      use_growth_projections: true
    };

    logger.log('Requesting Localytics Scores with options:', requestData);

    const response = await localyticsAxios.post('/revenue/localytics-scores', requestData, {
      params: { user: user.email },
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.data) {
      throw new Error('No data received from server');
    }

    logger.log('Localytics scoring completed:', {
      businesses: response.data.total_businesses_analyzed,
      avgScore: response.data.summary_stats?.average_score,
      highPerformers: response.data.summary_stats?.high_performers
    });

    return response.data;
  } catch (error) {
    logger.error("Error in getLocalyticsScores:", error);
    if (error.response) {
      logger.error("Server response:", error.response.data);
      throw new Error(error.response.data.detail || error.response.data.message || 'Server error');
    }
    throw error;
  }
}

/**
 * Format Localytics Score for display
 * @param {number} score - Localytics Score (0-100 scale)
 * @param {boolean} showRelative - Whether to include relative description
 * @returns {string} Formatted score string
 */
export function formatLocalyticsScore(score, showRelative = false) {
  if (score === null || score === undefined) {
    return 'N/A';
  }

  const formattedScore = Math.round(score).toString();

  if (!showRelative) {
    return formattedScore;
  }

  // Calculate relative performance as percentage above/below median (50)
  const diff = score - 50;
  let relativeDesc = '';

  if (diff >= 0) {
    relativeDesc = diff === 0 ? 'at median' : `${Math.round(diff)} above median`;
  } else {
    relativeDesc = `${Math.round(Math.abs(diff))} below median`;
  }

  return `${formattedScore} (${relativeDesc})`;
}


/**
 * Get Localytics Score color for charts (hex color)
 * Uses a sliding gradient: Vibrant Blue (high) → Muted Grey (low)
 * Higher scores have more intensity and saturation
 * @param {number} score - Localytics Score (0-100 scale)
 * @param {Object} businessData - Raw business data (unused, kept for API compatibility)
 * @returns {string} Hex color for charts and gradients
 */
export function getLocalyticsScoreColor(score, businessData = null) {
  // Clamp score to expected range
  const clampedScore = Math.max(0, Math.min(100, score));

  // Normalize to 0-1 for color interpolation
  const normalizedScore = clampedScore / 100;

  // Blue-to-grey gradient with aggressive intensity falloff
  // High scores: Electric, eye-catching blue that pops
  // Low scores: Muted, flat grey
  const electricBlue = [0, 136, 255];   // #0088FF - Electric blue, maximum pop
  const mutedGrey = [156, 163, 184];    // #9CA3B8 - Flat muted grey

  // Apply aggressive easing - only top performers stay vibrant blue
  // Exponent of 3 means scores need to be very high to show blue
  const easedScore = Math.pow(normalizedScore, 3);

  // Interpolate between muted grey (low) and electric blue (high)
  const r = Math.round(mutedGrey[0] + (electricBlue[0] - mutedGrey[0]) * easedScore);
  const g = Math.round(mutedGrey[1] + (electricBlue[1] - mutedGrey[1]) * easedScore);
  const b = Math.round(mutedGrey[2] + (electricBlue[2] - mutedGrey[2]) * easedScore);

  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/**
 * Get score color scheme for UI components (Chakra UI compatible)
 * Uses blue-grey gradient matching the chart colors
 * @param {number} score - Localytics Score (0-100 scale)
 * @returns {Object} Color scheme info with Chakra UI color names
 */
export function getLocalyticsScoreColorScheme(score) {
  const clampedScore = Math.max(0, Math.min(100, score));

  if (clampedScore >= 70) {
    return { color: 'blue', description: `Score: ${clampedScore}` };
  } else if (clampedScore >= 40) {
    return { color: 'blue', description: `Score: ${clampedScore}` };
  } else {
    return { color: 'gray', description: `Score: ${clampedScore}` };
  }
}

/**
 * Calculate portfolio-level Localytics insights
 * @param {Array} scoreResults - Array of Localytics Score results
 * @returns {Object} Portfolio insights and metrics
 */
export function calculateLocalyticsInsights(scoreResults) {
  if (!scoreResults || scoreResults.length === 0) {
    return null;
  }

  const totalBusinesses = scoreResults.length;
  const avgScore = scoreResults.reduce((sum, result) => sum + result.localytics_score, 0) / totalBusinesses;

  // Find top performers (top 20% by score)
  const sortedByScore = [...scoreResults].sort((a, b) => b.localytics_score - a.localytics_score);
  const topPerformers = sortedByScore.slice(0, Math.max(1, Math.ceil(totalBusinesses * 0.2)));

  // Find improvement opportunities (bottom 20% with recommendations)
  const improvementOpportunities = sortedByScore
    .slice(-Math.max(1, Math.ceil(totalBusinesses * 0.2)))
    .filter(result => result.recommendations?.length > 0)
    .slice(0, 5);

  // Component analysis - find strongest and weakest areas
  const componentStrengths = {
    recent_reviews: 0,
    total_reviews: 0,
    sentiment: 0,
    traffic: 0
  };

  scoreResults.forEach(result => {
    if (result.components) {
      componentStrengths.recent_reviews += result.components.recent_reviews_score || 0;
      componentStrengths.total_reviews += result.components.total_reviews_score || 0;
      componentStrengths.sentiment += result.components.sentiment_score || 0;
      componentStrengths.traffic += result.components.traffic_score || 0;
    }
  });

  // Average component strengths
  Object.keys(componentStrengths).forEach(component => {
    componentStrengths[component] = Math.round(componentStrengths[component] / totalBusinesses);
  });

  return {
    totalBusinesses,
    averageScore: Math.round(avgScore),
    topPerformers: topPerformers.map(result => ({
      name: result.business_name,
      score: result.localytics_score,
      rank: sortedByScore.findIndex(r => r.business_name === result.business_name) + 1
    })),
    improvementOpportunities: improvementOpportunities.map(result => ({
      name: result.business_name,
      score: result.localytics_score,
      topRecommendation: result.recommendations?.[0]
    })),
    componentStrengths,
    scoreRange: {
      highest: sortedByScore[0]?.localytics_score || 0,
      lowest: sortedByScore[sortedByScore.length - 1]?.localytics_score || 0,
      median: sortedByScore[Math.floor(sortedByScore.length / 2)]?.localytics_score || 50,
      quartiles: {
        q1: sortedByScore[Math.floor(sortedByScore.length * 0.25)]?.localytics_score || 0,
        q3: sortedByScore[Math.floor(sortedByScore.length * 0.75)]?.localytics_score || 0
      }
    }
  };
}

// Export all functions
const localyticsScoreService = {
  getLocalyticsScores,
  formatLocalyticsScore,
  getLocalyticsScoreColor,
  getLocalyticsScoreColorScheme,
  calculateLocalyticsInsights
};

export default localyticsScoreService;
